import { useState, useEffect, useCallback, useReducer } from 'react';
import type { SortingState } from '@tanstack/react-table';

import Search from 'components/partials/search/search';
import TableComponent from 'components/partials/table-component/table-component';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';
import { usersReducer } from 'containers/admin/reducer';
import { SEARCH_CHANGE, PAGE_CHANGE } from 'containers/admin/actions';
import {
  getIncludedReportsTableSettings,
  type IncludedReportRow,
} from 'containers/admin/products/product/included-reports/included-reports-table-settings';
import RadioInput from 'components/partials/radio-input/radio-input';

import {
  useAdminReportsList,
  useModifyReport,
} from 'hooks/api/use-admin-reports';

interface IncludedReportsProps {
  productId: string | number;
  userId: string;
  initialLoading: boolean;
}

interface ReducerState {
  search: string;
  pageIndex: number;
}

const initialState: ReducerState = {
  search: '',
  pageIndex: 0,
};

interface TableAmountState {
  pageSize: number;
  totalRecords: number;
}

interface RadioOption {
  label: string;
  value: string;
  colorClass?: string;
}

const radioOptions: RadioOption[] = [
  { label: 'Linked', value: 'linked' },
  { label: 'All available', value: 'all' },
];

const IncludedReports = ({
  productId,
  userId,
  initialLoading,
}: IncludedReportsProps): React.JSX.Element => {
  const modifyReportMutation = useModifyReport();

  const [state, dispatch] = useReducer(usersReducer, initialState);
  const { search, pageIndex } = state as ReducerState;

  const [{ pageSize }, setAmount] = useState<TableAmountState>({
    pageSize: 20,
    totalRecords: 0,
  });

  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: 'title', desc: false },
  ]);

  const [filterOption, setFilterOption] = useState<string>('linked');

  const handleSearch = useCallback((payload: string): void => {
    dispatch({ type: SEARCH_CHANGE, payload });
  }, []);
  const handlePagination = useCallback((payload: number): void => {
    dispatch({ type: PAGE_CHANGE, payload });
  }, []);

  const filters = {
    title: { contains: search },
    ...(filterOption === 'linked' && { productId }),
    ...(filterOption === 'all' && { productId: { specified: 'false' } }),
  };

  const { data: reportsResponse, isFetching } = useAdminReportsList({
    pageSize,
    pageIndex,
    sortBy: sortingParams,
    filters,
  });

  const allReports = (reportsResponse?.data ?? []) as IncludedReportRow[];
  const totalRecords = reportsResponse?.pagination?.totalRecords ?? 0;

  useEffect(() => {
    dispatch({ type: PAGE_CHANGE, payload: 0 });
  }, [filterOption]);

  const handleToggleChange = async (
    report: IncludedReportRow,
    newToggleValue: boolean
  ): Promise<void> => {
    const { title, description, powerBiId } = report;

    try {
      await modifyReportMutation.mutateAsync({
        userId,
        reportId: report.id,
        reportData: {
          title: title ?? '',
          description,
          powerBiId: String(powerBiId ?? ''),
          productId: newToggleValue ? Number(productId) : undefined,
        },
      });
    } catch (error) {
      console.error('Error updating report', error);
    }
  };

  const columns = getIncludedReportsTableSettings(
    productId,
    handleToggleChange
  );

  const isBusy = isFetching || modifyReportMutation.isPending;

  const renderContent = (): React.JSX.Element | null => {
    if (allReports.length > 0 || (isBusy && !initialLoading)) {
      return (
        <TableComponent
          columns={columns}
          isLoading={isBusy}
          data={allReports}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPagination={handlePagination}
          setAmount={setAmount}
          sortingParams={sortingParams}
          setSortingParams={setSortingParams}
          totalRecords={totalRecords}
          customHeight="max-h-[50vh]"
        />
      );
    }

    if (!isBusy && !initialLoading && allReports.length === 0) {
      return (
        <EmptySpaceContent
          text="There are no reports to show."
          customClass="!mt-14 !h-[350px]"
        />
      );
    }

    return null;
  };

  return (
    <>
      <div className="relative">
        <div className="my-4">
          <Search setSearch={handleSearch} />
        </div>
        <div className="flex gap-5 absolute z-10">
          <div className="font-semibold text-gray-600">Show</div>
          <div className="flex">
            {radioOptions.map((option) => (
              <RadioInput
                key={option.value}
                labelFor={option.value}
                label={option.label}
                radioValue={option.value}
                setAction={(val) => setFilterOption(val ?? '')}
                onChange={(val) => setFilterOption(val ?? '')}
                value={filterOption}
                customClass="mr-4"
                labelClass={option.colorClass}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="relative mt-4">{renderContent()}</div>
    </>
  );
};

export default IncludedReports;
